import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import useApiContext from 'hooks/useApiContext';
import { iKeyCount, iTicketingStats } from 'services/Ticketing/Ticketing.type';
import {
  card,
  CategoryChip,
  formatMoney,
  formatShortDay,
  PAGE_SX,
  sectionTitle,
  TicketingNav,
} from './common';
import { countryName, flag } from './countries';
import { eRoles } from 'interfaces/Roles';

// One series per chart: a single hue, no legend (the title names it)
const BAR = '#3b82f6';

function Tile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string | number;
  sub?: string;
}): JSX.Element {
  return (
    <Box sx={{ ...card, p: 2 }}>
      <Typography
        sx={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: '#666',
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 26, fontWeight: 700 }}>{value}</Typography>
      {sub && (
        <Typography sx={{ fontSize: 12, color: '#888' }}>{sub}</Typography>
      )}
    </Box>
  );
}

function Ranking({
  title,
  rows,
  render,
}: {
  title: string;
  rows: iKeyCount[];
  render?: (k: string) => string;
}): JSX.Element {
  const max = Math.max(1, ...rows.map((r) => r.n));
  const total = rows.reduce((s, r) => s + r.n, 0);
  return (
    <Box sx={card}>
      <Typography sx={{ ...sectionTitle, mb: 1.5 }}>{title}</Typography>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#888' }}>
          Pas encore de données.
        </Typography>
      ) : (
        rows.map((r) => (
          <Box
            key={r.key}
            sx={{ mb: 1 }}
            title={`${render ? render(r.key) : r.key} : ${r.n}`}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
              }}
            >
              <span
                style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {render ? render(r.key) : r.key}
              </span>
              <span style={{ color: '#555' }}>
                {r.n}{' '}
                <span style={{ color: '#aaa' }}>
                  · {Math.round((r.n / total) * 100)} %
                </span>
              </span>
            </Box>
            <Box
              sx={{ height: 6, bgcolor: '#f1f1f1', borderRadius: 1, mt: 0.4 }}
            >
              <Box
                sx={{
                  height: 6,
                  borderRadius: 1,
                  width: `${(r.n / max) * 100}%`,
                  bgcolor: BAR,
                }}
              />
            </Box>
          </Box>
        ))
      )}
    </Box>
  );
}

function Columns({
  title,
  rows,
  label,
  empty,
}: {
  title: string;
  rows: Array<{ key: string; n: number }>;
  label: (k: string) => string;
  empty: string;
}): JSX.Element {
  const max = Math.max(1, ...rows.map((r) => r.n));
  const [hover, setHover] = useState<number | null>(null);
  return (
    <Box sx={card}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography sx={sectionTitle}>{title}</Typography>
        <Typography sx={{ fontSize: 13, color: '#555' }}>
          {hover !== null && rows[hover]
            ? `${label(rows[hover].key)} : ${rows[hover].n}`
            : rows.length
            ? `max ${max}`
            : ''}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography sx={{ fontSize: 14, color: '#888' }}>{empty}</Typography>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: '2px',
              height: 150,
              borderBottom: '1px solid #e5e5e5',
            }}
            onMouseLeave={() => setHover(null)}
          >
            {rows.map((r, i) => (
              <Box
                key={r.key}
                sx={{
                  flex: 1,
                  height: '100%',
                  display: 'flex',
                  alignItems: 'flex-end',
                }}
                onMouseEnter={() => setHover(i)}
                title={`${label(r.key)} : ${r.n}`}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: `${(r.n / max) * 100}%`,
                    minHeight: r.n ? 2 : 0,
                    bgcolor: BAR,
                    borderRadius: '4px 4px 0 0',
                    opacity: hover === null || hover === i ? 1 : 0.45,
                  }}
                />
              </Box>
            ))}
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: 12,
              color: '#888',
              mt: 0.5,
            }}
          >
            <span>{label(rows[0].key)}</span>
            <span>{label(rows[rows.length - 1].key)}</span>
          </Box>
        </>
      )}
    </Box>
  );
}

/** Visitor data of an event. */
export default function TicketingStats(): JSX.Element {
  const { eventId = '' } = useParams();
  const { ticketingRepository, userRepository } = useApiContext();
  const [d, setD] = useState<iTicketingStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hourDay, setHourDay] = useState<string | null>(null);
  useEffect(() => {
    ticketingRepository
      .getStats(eventId)
      .then((r) => {
        setD(r);
        setHourDay(
          r.entries_by_hour[r.entries_by_hour.length - 1]?.day ?? null
        );
      })
      .catch((e: Error) => setError(e.message));
  }, [eventId]);
  if (!d)
    return (
      <Box sx={PAGE_SX}>
        {error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}
      </Box>
    );

  const hours = d.entries_by_hour.find((x) => x.day === hourDay)?.hours ?? [];
  const minH = hours.length ? Math.min(...hours.map((h) => h.hour)) : 0;
  const maxH = hours.length ? Math.max(...hours.map((h) => h.hour)) : -1;
  const hourRows = Array.from({ length: maxH - minH + 1 }, (_, i) => ({
    key: String(minH + i),
    n: hours.find((h) => h.hour === minH + i)?.n ?? 0,
  }));
  const attendance = d.totals.tickets
    ? Math.round((d.totals.entered / d.totals.tickets) * 100)
    : 0;
  const country = (k: string): string => `${flag(k)} ${countryName(k)}`;

  return (
    <Box sx={PAGE_SX}>
      <TicketingNav
        current="stats"
        canConfigure={userRepository.getUser()?.role !== eRoles.MODERATOR}
        tickets={d.event.tickets}
        eventName={d.event.name}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        <Tile
          label="Billets valides"
          value={d.totals.tickets}
          sub={`${d.totals.cancelled} annulé(s)`}
        />
        <Tile
          label="Entrés"
          value={d.totals.entered}
          sub={`${attendance} % de présence`}
        />
        <Tile
          label="Chiffre d'affaires TTC"
          value={formatMoney(d.totals.revenue_cents, d.currency)}
          sub={`${d.totals.paid_orders} paiement(s) · ${formatMoney(
            d.totals.discount_cents,
            d.currency
          )} de remises`}
        />
        <Tile
          label="Sociétés"
          value={d.totals.companies}
          sub={`${d.totals.invitations} invitation(s) · ${d.totals.optin} opt-in`}
        />
      </Box>
      <Box sx={{ ...card, mb: 3 }}>
        <Typography sx={{ ...sectionTitle, mb: 1 }}>
          Par type de billet
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Billet</TableCell>
              <TableCell>Émis</TableCell>
              <TableCell>Quota</TableCell>
              <TableCell>Entrés</TableCell>
              <TableCell>Présence</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {d.by_type.map((t) => (
              <TableRow key={t.name}>
                <TableCell>
                  <CategoryChip category={t.category} />{' '}
                  <span style={{ marginLeft: 6 }}>{t.name}</span>
                </TableCell>
                <TableCell>{t.tickets}</TableCell>
                <TableCell sx={{ color: '#888' }}>{t.quota ?? '∞'}</TableCell>
                <TableCell>{t.entered}</TableCell>
                <TableCell>
                  {t.tickets ? Math.round((t.entered / t.tickets) * 100) : 0} %
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: 3,
          mb: 3,
        }}
      >
        <Columns
          title="Inscriptions par jour"
          rows={d.registrations.map((r) => ({ key: r.day, n: r.n }))}
          label={(k) => formatShortDay(k)}
          empty="Pas encore d'inscription."
        />
        <Box>
          <Columns
            title={`Entrées par heure${
              hourDay ? ` · ${formatShortDay(hourDay)}` : ''
            }`}
            rows={hourRows}
            label={(h) => `${h} h`}
            empty="Aucune entrée scannée."
          />
          {d.entries_by_hour.length > 1 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              {d.entries_by_hour.map((x) => (
                <Box
                  key={x.day}
                  role="button"
                  tabIndex={0}
                  onClick={() => setHourDay(x.day)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: 12,
                    px: 1,
                    py: 0.3,
                    borderRadius: 999,
                    border: '1px solid #ddd',
                    bgcolor: x.day === hourDay ? '#111' : '#fff',
                    color: x.day === hourDay ? '#fff' : '#111',
                  }}
                >
                  {formatShortDay(x.day)}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
          gap: 3,
        }}
      >
        <Ranking title="Nationalités" rows={d.nationalities} render={country} />
        <Ranking
          title="Pays de résidence"
          rows={d.countries}
          render={country}
        />
        <Ranking title="Secteurs" rows={d.industries} />
        <Ranking title="Sociétés les plus représentées" rows={d.companies} />
        <Ranking title="Fonctions" rows={d.job_titles} />
        <Ranking
          title="Entrées par point de contrôle"
          rows={(d.entries_by_checkpoint ?? []).map((x) => ({
            key: x.name,
            n: x.n,
          }))}
        />
        <Box sx={card}>
          <Typography sx={{ ...sectionTitle, mb: 1 }}>
            Entrées par jour et refus
          </Typography>
          {d.entries_by_day.length === 0 && (
            <Typography sx={{ fontSize: 14, color: '#888' }}>
              Aucune entrée.
            </Typography>
          )}
          {d.entries_by_day.map((x) => (
            <Box
              key={x.day}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 14,
              }}
            >
              <span>{formatShortDay(x.day)}</span>
              <b>{x.n}</b>
            </Box>
          ))}
          <Box sx={{ borderTop: '1px solid #eee', mt: 1.5, pt: 1.5 }}>
            {d.refused.map((r) => (
              <Box
                key={r.result}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 13,
                  color: '#555',
                }}
              >
                <span>✕ {r.label}</span>
                <span>{r.n}</span>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
